'use client';

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { useEffect, useRef, useState, useCallback } from 'react';

interface OrderNotification {
  order_code: string;
  store_name: string;
  order_status: string;
  total_payment: number;
  time: number;
}

interface UseOrderNotificationOptions {
  token: string;
  onMessage?: (data: OrderNotification) => void;
}

export function useOrderNotificationSSE({
  token,
  onMessage,
}: UseOrderNotificationOptions) {
  const [messages, setMessages] = useState<OrderNotification[]>([]);
  const controllerRef = useRef<AbortController | null>(null);

  // callback cố định (không re-declare mỗi render)
  const handleMessage = useCallback(
    (data: OrderNotification) => {
      setMessages((prev) => {
        // tránh push trùng y hệt (order_code + time giống nhau)
        const isDuplicate = prev.some(
          (m) => m.order_code === data.order_code && m.time === data.time
        );
        if (isDuplicate) return prev;
        return [...prev, data];
      });

      // Gọi callback nếu có
      onMessage?.(data);
    },
    [onMessage]
  );

  useEffect(() => {
    if (!token) return;

    const controller = new AbortController();
    controllerRef.current = controller;

    void fetchEventSource(
      process.env.NEXT_PUBLIC_ORDER_NOTIFICATION_API ??
        'https://api.ahamove.com/api/v3/private/food-order/customer/order-notification',
      {
        method: 'GET',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        // eslint-disable-next-line @typescript-eslint/require-await
        onopen: async (res) => {
          if (res.ok) {
            console.log('✅ SSE connected');
          } else {
            console.error('❌ SSE failed', res.status);
          }
        },
        onmessage: (ev) => {
          try {
            // Skip empty or whitespace-only data
            if (!ev.data || ev.data.trim() === '') {
              return;
            }
            const data: OrderNotification = JSON.parse(ev.data);
            handleMessage(data);
          } catch (err) {
            console.error('❌ Parse error', err, 'Raw data:', ev.data);
          }
        },
        onerror: (err) => {
          console.error('❌ SSE error', err);
          controller.abort();
        },
      }
    );

    return () => {
      controller.abort();
      console.log('🔌 SSE disconnected');
    };
  }, [token, handleMessage]);

  return { messages };
}
